import React, { useState } from 'react';

import { Button } from 'components/ui/button';
import { Errors } from 'components/ui/errors';
import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { digitsIn, messageFor } from './helpers';
import { post } from 'helpers/api';
import { formatTime } from 'helpers/formatters/date_time_formats';

import type { PhoneEnrollment } from 'components/users/types';

interface Props {
  codeExpiresAt: string | null;
  number: string;
  path: string;
  onClose: () => void;
  onConfirmed: (enrollment: PhoneEnrollment) => void;
  onRestart: () => void;
}

export function ConfirmationForm({ codeExpiresAt, number, path, onClose, onConfirmed, onRestart }: Props) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function confirmCode() {
    setBusy(true);
    setError(null);

    try {
      const enrollment: PhoneEnrollment = await post(path, { code });
      onConfirmed(enrollment);
    } catch (thrown) {
      setError(messageFor(thrown));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  function handleCodeChange(event: React.ChangeEvent<HTMLInputElement>) {
    setCode(event.target.value);
  }

  const submittable = digitsIn(code) === 6;

  let description: string;
  if (!codeExpiresAt) {
    description = `Sent to ${number}.`;
  } else if (new Date(codeExpiresAt) <= new Date()) {
    description = `The code sent to ${number} has expired.`;
  } else {
    description = `Sent to ${number}. It expires at ${formatTime(codeExpiresAt)}.`;
  }

  return <>
    <Modal.Header>
      <Modal.Title>Confirm your phone</Modal.Title>
    </Modal.Header>

    <Modal.Body>
      <Field className="form-group">
        <Field.Label htmlFor="phoneEnrollmentCode">Confirmation code</Field.Label>
        <Input
          autoComplete="one-time-code"
          id="phoneEnrollmentCode"
          inputMode="numeric"
          value={code}
          onChange={handleCodeChange}
        />
        <Field.Description>{description}</Field.Description>
      </Field>

      <Errors error={error} />
    </Modal.Body>

    <Modal.Footer>
      <Button className="me-auto" disabled={busy} label="Send a new code" variant="ghost" onClick={onRestart} />
      <Button disabled={busy} label="Cancel" variant="ghost" onClick={onClose} />
      <Button disabled={busy || !submittable} label="Confirm" onClick={confirmCode} />
    </Modal.Footer>
  </>;
}
